import { getUnixTime, format } from 'date-fns';
import { defaultCurrencyId } from '@/creditflowresearch/ui/components/portfolioMonitor/constants';
import { IPortfolioItem } from '@/creditflowresearch/domain/vo/PortfolioMonitor/IPortfoliosCollection';
import {
  ICreatePortfolioFormState,
  ICurrencyValue,
} from '@/creditflowresearch/domain/vo/PortfolioMonitor/ICreatePortfolioFormState';
import { ICreatePortfolioPayload } from '@/creditflowresearch/domain/vo/PortfolioMonitor/ICreatePortfolioPayload';
import { ICurrency } from '@/creditflowresearch/domain/vo/ICurrency';
import { v4 as uuid } from 'uuid';
import { removeDelimiters } from '@framework/data-helpers/convertTypes';
import { YEAR_MONTH_DAY_SLASH_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import { DEFAULT_TIME_ZONE } from '@framework/timezone/constants';
import { deprecatedParseLegacyDateString } from '@framework/date-helpers/parseDateString';

export const mapCurrencyToOption = (currency: ICurrency): ICurrencyValue => ({
  value: currency.id,
  label: currency.displayName,
  currencySymbol: currency.abbreviation,
  countryCode: currency.flagDisplayName,
});

export const normalizeCreatePortfolioPayload = (payload: ICreatePortfolioFormState): ICreatePortfolioPayload => ({
  ...payload,
  lists: payload.lists.map((list, index) => ({
    ...list,
    priority: list?.priority || index + 1,
    positions: list?.positions?.length
      ? list.positions.map(({
        datePurchased,
        positionSize,
        dollarPricePaid,
        currencyId,
        id,
        name,
        cusip,
        bond,
      }) => ({
        id,
        cusip,
        name: typeof name === 'string' ? name : (name as { label: string })?.label || null,
        datePurchased: datePurchased ? getUnixTime(new Date(datePurchased)) : null,
        positionSize: Number(removeDelimiters(positionSize)) || null,
        dollarPricePaid: Number(dollarPricePaid) || null,
        currencyId: typeof currencyId === 'string' ? currencyId : (currencyId?.value || defaultCurrencyId),
        bond: {
          dealId: bond.dealId,
          trancheId: bond.trancheId,
          customBondName: bond.customBondName === '' ? null : bond.customBondName?.slice(0, 50) || null,
        },
    })) : [],
  })),
});

export const normalizePortfolioToFormState = (
    portfolio: IPortfolioItem, currencies: ICurrency[] = [],
): ICreatePortfolioFormState => ({
  ...portfolio,
  lists: portfolio?.lists.length ? portfolio?.lists.map(({ positions = [], ...list }) => ({
    ...list,
    positions: positions.map(({
      positionSize,
      dollarPricePaid,
      currencyId,
      mappedDealId,
      mappedTrancheId,
      customBondName,
      ...position
    }) => {
      const currencyItem = currencies.find(
          (currency) => (currency.id === currencyId) || (currency.id === defaultCurrencyId),
      );
      return ({
        ...position,
        datePurchased: position.datePurchased
            ? format(
              deprecatedParseLegacyDateString(position.datePurchased, DEFAULT_TIME_ZONE.timeZone),
              YEAR_MONTH_DAY_SLASH_FORMAT,
            ) : null,
        positionSize: positionSize ? positionSize.toString() : '',
        dollarPricePaid: dollarPricePaid ? dollarPricePaid.toString() : '',
        currencyId: currencyItem
            ? mapCurrencyToOption(currencyItem)
            : { value: currencyId, label: currencyId },
        bond: {
          dealId: mappedDealId,
          trancheId: mappedTrancheId,
          customBondName: customBondName || '',
        },
      });
    }),
  })) : [{
    id: uuid(),
    name: '',
    positions: [],
    priority: 1,
    isLocal: true,
  }],
});

export const isQueryStringHasValidLength = (query: string) => query.length >= 3 && query.length <= 128;
